
import { useState } from 'react';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'Sarah Chen',
      company: 'LocalCraft Bakery',
      role: 'Founder & CEO',
      image: 'https://public.readdy.ai/ai/img_res/e1cc5892bbe2158b2cc3b52df4895963.jpg',
      quote: 'RankDominate transformed our local bakery into a thriving online business. We went from zero online presence to dominating local search results and shipping nationwide. The results speak for themselves - 400% revenue growth in just 6 months.',
      metrics: { revenue: '400%', ranking: '#1 Local', timeframe: '6 months' }
    },
    {
      name: 'Marcus Rodriguez',
      company: 'TechGear Pro',
      role: 'E-commerce Director',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20male%20business%20executive%20in%20modern%20tech%20office%20environment%2C%20confident%20technology%20entrepreneur%20portrait%20with%20sleek%20contemporary%20setting&width=400&height=400&seq=marcus-testimonial&orientation=squarish',
      quote: 'Our e-commerce store was struggling to get visibility despite having great products. RankDominate\'s SEO strategies got us ranking #1 for over 50 high-value keywords. Our organic traffic increased by 800% and conversions are through the roof.',
      metrics: { traffic: '800%', keywords: '50+', conversion: '12.5%' }
    },
    {
      name: 'Jennifer Walsh',
      company: 'FitLife Supplements',
      role: 'Marketing Director',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Caucasian%20female%20fitness%20industry%20executive%20in%20modern%20health%20supplement%20company%20office%2C%20confident%20business%20leader%20portrait%20with%20clean%20contemporary%20background&width=400&height=400&seq=jennifer-testimonial&orientation=squarish',
      quote: 'We needed to scale across the US market and RankDominate delivered beyond our expectations. Their growth strategies helped us expand to 15 states with a 600% revenue increase. They truly understand how to dominate markets.',
      metrics: { growth: '600%', states: '15', customers: '10,000+' }
    }
  ];

  return (
    <section className="py-20 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-indigo-500/20 border border-indigo-500/30 rounded-full text-indigo-300 text-sm font-medium mb-6">
            <i className="ri-chat-quote-line mr-2"></i>
            Client Testimonials
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            What Our Clients
            <span className="text-indigo-400"> Say About Us</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Don't just take our word for it. Hear from the business owners who've experienced transformational growth.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 md:p-12 mb-12 border border-white/10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Image */}
            <div className="flex-shrink-0">
              <img 
                src={testimonials[activeTestimonial].image}
                alt={testimonials[activeTestimonial].name}
                className="w-32 h-32 rounded-full object-cover border-4 border-indigo-500/30"
              />
            </div>

            {/* Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="mb-6">
                <i className="ri-double-quotes-l text-4xl text-indigo-400 mb-4 block"></i>
                <p className="text-xl md:text-2xl text-gray-100 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {testimonials[activeTestimonial].quote}
                </p>
              </div>

              <div className="mb-6">
                <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {testimonials[activeTestimonial].name}
                </h4>
                <p className="text-indigo-300" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {testimonials[activeTestimonial].role}, {testimonials[activeTestimonial].company}
                </p>
              </div>

              {/* Metrics */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6">
                {Object.entries(testimonials[activeTestimonial].metrics).map(([key, value], index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-indigo-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {value}
                    </div>
                    <div className="text-sm text-gray-400 capitalize" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Navigation */}
        <div className="flex justify-center gap-4">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveTestimonial(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                index === activeTestimonial 
                  ? 'bg-indigo-500 scale-125' 
                  : 'bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>

        {/* All Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              onClick={() => setActiveTestimonial(index)}
              className={`bg-white/5 backdrop-blur-sm rounded-2xl p-6 border transition-all duration-300 cursor-pointer ${
                index === activeTestimonial 
                  ? 'border-indigo-500/50 bg-indigo-500/10' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h5 className="font-semibold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {testimonial.name}
                  </h5>
                  <p className="text-sm text-gray-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {testimonial.company}
                  </p>
                </div>
              </div>
              <p className="text-gray-300 text-sm line-clamp-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                {testimonial.quote}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
